// ================================================================
// src/freeStudy/storage.js
// لایه‌ی persistence — Server-backed (json-server)
// ----------------------------------------------------------------
// • خرید و پیشرفت در سرور ذخیره می‌شوند (منبع حقیقت)
// • withLock فقط برای جلوگیری از race بین تب‌های همین مرورگر
// • migration خودکار از localStorage قدیمی به سرور (یک‌بار)
// ================================================================
import { recomputeDerived } from './core.js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ================================================================
// کلیدها — فقط برای lock و migration از localStorage قدیمی
// ================================================================
const KEYS = {
  purchase: (uid, cid) => `freestudy-purchase-v2-${uid}-${cid}`,
  progress: (uid, cid) => `freestudy-progress-v2-${uid}-${cid}`,
  progressLegacy: (uid, cid) => `freestudy-progress-${uid}-${cid}`,
  lock: (uid, cid) => `freestudy-lock-${uid}-${cid}`,
};

// ================================================================
// A) Locking — جلوگیری از race بین تب‌های همین مرورگر
//    ⚠️ به سرور نمی‌رود (device-local)
// ================================================================
export async function withLock(userId, courseId, fn) {
  const key = KEYS.lock(userId, courseId);

  // اولویت ۱: Web Locks API (استاندارد، همه‌ی مرورگرهای مدرن)
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(key, fn);
  }

  // Fallback: mutex نرم با timestamp در localStorage
  const myToken = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const STALE_MS = 5000;
  const start = Date.now();

  while (true) {
    const raw = localStorage.getItem(key);
    const now = Date.now();
    let canTake = true;

    if (raw) {
      try {
        const info = JSON.parse(raw);
        if (now - info.ts < STALE_MS) canTake = false;
      } catch {
        canTake = true;
      }
    }

    if (canTake) {
      localStorage.setItem(
        key,
        JSON.stringify({ token: myToken, ts: now })
      );
      try {
        return await fn();
      } finally {
        try {
          const check = localStorage.getItem(key);
          if (check) {
            const info = JSON.parse(check);
            if (info.token === myToken) localStorage.removeItem(key);
          }
        } catch {}
      }
    }

    if (Date.now() - start > 8000) {
      throw new Error('قفل ذخیره‌سازی آزاد نشد — لطفاً دوباره تلاش کنید');
    }
    await new Promise((r) => setTimeout(r, 50 + Math.random() * 100));
  }
}

// ================================================================
// B) Integrity — checksum سبک (فقط برای migration)
// ================================================================
function simpleChecksum(obj) {
  const json = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < json.length; i++) {
    hash = (hash << 5) - hash + json.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
}

// ================================================================
// C) Raw read/write — فقط برای lock و migration
// ================================================================
export function readRaw(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.warn('⚠️ Failed to parse storage:', key, e);
    return null;
  }
}

export function writeRaw(key, data) {
  try {
    const payload = { ...data };
    delete payload.checksum;
    payload.checksum = simpleChecksum(payload);
    localStorage.setItem(key, JSON.stringify(payload));
    return true;
  } catch (e) {
    console.error('⚠️ Failed to write storage:', key, e);
    return false;
  }
}

// ================================================================
// D) Purchase State — Server-backed
// ================================================================
export function emptyPurchaseState(userId, courseId) {
  return {
    schema: 'fsp-v2',
    userId,
    courseId,
    revision: 0,
    lastUpdated: 0,
    transactions: [],
    unlockedVideoIds: [],
    totalPaid: 0,
    totalRefunded: 0,
  };
}

/** خواندن وضعیت خرید از سرور (async) */
export async function getPurchaseState(userId, courseId) {
  if (!userId || !courseId) return emptyPurchaseState(userId, courseId);

  try {
    const res = await fetch(
      `${API_BASE}/freeStudyPurchases?userId=${encodeURIComponent(userId)}&courseId=${encodeURIComponent(courseId)}`
    );
    const data = await res.json();
    const rec = Array.isArray(data) ? data[0] : null;

    if (rec) {
      const derived = recomputeDerived(rec.transactions || []);
      return {
        schema: 'fsp-v2',
        userId,
        courseId,
        revision: rec.revision || 0,
        lastUpdated: rec.lastUpdated || 0,
        transactions: rec.transactions || [],
        ...derived,
      };
    }

    // 🔄 Migration: اگر در localStorage قدیمی هست، به سرور منتقل کن
    const legacy = readRaw(KEYS.purchase(userId, courseId));
    if (legacy) {
      const migrated = {
        schema: 'fsp-v2',
        userId,
        courseId,
        revision: legacy.revision || 0,
        lastUpdated: legacy.lastUpdated || Date.now(),
        transactions: legacy.transactions || [],
      };
      try {
        await fetch(`${API_BASE}/freeStudyPurchases`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(migrated),
        });
        try { localStorage.removeItem(KEYS.purchase(userId, courseId)); } catch {}
      } catch (e) {
        console.warn('⚠️ purchase migration failed:', e);
      }
      const derived = recomputeDerived(migrated.transactions);
      return { ...migrated, ...derived };
    }

    return emptyPurchaseState(userId, courseId);
  } catch (e) {
    console.warn('⚠️ getPurchaseState failed:', e);
    return emptyPurchaseState(userId, courseId);
  }
}

/** ذخیره وضعیت خرید در سرور (async) */
export async function persistPurchaseState(state) {
  const payload = {
    schema: 'fsp-v2',
    userId: state.userId,
    courseId: state.courseId,
    revision: (state.revision || 0) + 1,
    lastUpdated: Date.now(),
    transactions: state.transactions || [],
  };

  try {
    const check = await fetch(
      `${API_BASE}/freeStudyPurchases?userId=${encodeURIComponent(state.userId)}&courseId=${encodeURIComponent(state.courseId)}`
    );
    const arr = await check.json();
    const rec = Array.isArray(arr) ? arr[0] : null;

    if (rec) {
      await fetch(`${API_BASE}/freeStudyPurchases/${rec.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      await fetch(`${API_BASE}/freeStudyPurchases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
  } catch (e) {
    console.warn('⚠️ persistPurchaseState failed:', e);
  }

  return {
    ...state,
    revision: payload.revision,
    lastUpdated: payload.lastUpdated,
  };
}

export async function mutatePurchaseState(userId, courseId, mutator) {
  return withLock(userId, courseId, async () => {
    const current = await getPurchaseState(userId, courseId);
    const next = mutator(current);
    if (!next) return current;
    return persistPurchaseState(next);
  });
}

// ================================================================
// E) Progress State — Server-backed
// ================================================================
export function emptyProgressState() {
  return {
    schema: 'fsprog-v2',
    completedVideoIds: [],
    notes: {},
    lastVideoId: null,
    lastVisited: null,
  };
}

/** خواندن وضعیت پیشرفت از سرور (async) */
export async function getProgressState(userId, courseId, course) {
  if (!userId || !courseId) return emptyProgressState();

  try {
    const res = await fetch(
      `${API_BASE}/freeStudyProgress?userId=${encodeURIComponent(userId)}&courseId=${encodeURIComponent(courseId)}`
    );
    const data = await res.json();
    const rec = Array.isArray(data) ? data[0] : null;

    if (rec) {
      return {
        schema: 'fsprog-v2',
        completedVideoIds: Array.isArray(rec.completedVideoIds)
          ? rec.completedVideoIds
          : [],
        notes: rec.notes || {},
        lastVideoId: rec.lastVideoId || null,
        lastVisited: rec.lastVisited || null,
      };
    }

    // 🔄 Migration: از localStorage جدید
    const legacy = readRaw(KEYS.progress(userId, courseId));
    if (legacy) {
      const migrated = {
        schema: 'fsprog-v2',
        userId,
        courseId,
        completedVideoIds: Array.isArray(legacy.completedVideoIds)
          ? legacy.completedVideoIds
          : [],
        notes: legacy.notes || {},
        lastVideoId: legacy.lastVideoId || null,
        lastVisited: legacy.lastVisited || null,
      };
      try {
        await fetch(`${API_BASE}/freeStudyProgress`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(migrated),
        });
        try { localStorage.removeItem(KEYS.progress(userId, courseId)); } catch {}
      } catch (e) {
        console.warn('⚠️ progress migration failed:', e);
      }
      return migrated;
    }

    // 🔄 Migration: از localStorage قدیمی (index-based)
    if (course) {
      const migrated = await migrateProgressLegacy(userId, courseId, course);
      if (migrated) return migrated;
    }

    return emptyProgressState();
  } catch (e) {
    console.warn('⚠️ getProgressState failed:', e);
    return emptyProgressState();
  }
}

/** ذخیره وضعیت پیشرفت در سرور (async) */
export async function persistProgressState(userId, courseId, state) {
  const payload = {
    schema: 'fsprog-v2',
    userId,
    courseId,
    completedVideoIds: state.completedVideoIds || [],
    notes: state.notes || {},
    lastVideoId: state.lastVideoId || null,
    lastVisited: state.lastVisited || null,
  };

  try {
    const check = await fetch(
      `${API_BASE}/freeStudyProgress?userId=${encodeURIComponent(userId)}&courseId=${encodeURIComponent(courseId)}`
    );
    const arr = await check.json();
    const rec = Array.isArray(arr) ? arr[0] : null;

    if (rec) {
      await fetch(`${API_BASE}/freeStudyProgress/${rec.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      await fetch(`${API_BASE}/freeStudyProgress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
  } catch (e) {
    console.warn('⚠️ persistProgressState failed:', e);
  }

  return payload;
}

export async function mutateProgressState(userId, courseId, course, mutator) {
  return withLock(userId, courseId, async () => {
    const current = await getProgressState(userId, courseId, course);
    const next = mutator(current);
    if (!next) return current;
    const result = await persistProgressState(userId, courseId, next);

    // 🆕 emit رویداد برای رفرش خودکار
    try {
      const { engine } = await import('../core/books/storage.js');
      engine.emit('progress:changed', { source: 'freestudy', userId, courseId });
      engine.broadcast('progress:changed', { source: 'freestudy', userId, courseId });
    } catch {}

    return result;
  });
}

// ================================================================
// F) Migration — از کلید قدیمی (index-based) به سرور
// ================================================================
async function migrateProgressLegacy(userId, courseId, course) {
  const legacy = readRaw(KEYS.progressLegacy(userId, courseId));
  if (!legacy) return null;

  const videos = course?.videos || [];
  const completedVideoIds = (legacy.completedVideos || [])
    .map((i) => videos[i]?.id)
    .filter(Boolean);

  const notes = {};
  Object.entries(legacy.notes || {}).forEach(([idx, text]) => {
    const vid = videos[Number(idx)]?.id;
    if (vid) notes[vid] = text;
  });

  const lastVideoId =
    legacy.lastVideoIndex != null
      ? videos[legacy.lastVideoIndex]?.id || null
      : null;

  const migrated = {
    schema: 'fsprog-v2',
    completedVideoIds,
    notes,
    lastVideoId,
    lastVisited: legacy.lastVisited || null,
  };

  // ذخیره در سرور
  await persistProgressState(userId, courseId, migrated);
  // پاک کردن localStorage قدیمی
  try { localStorage.removeItem(KEYS.progressLegacy(userId, courseId)); } catch {}

  console.log('✅ migrated progress from legacy:', userId, courseId);
  return migrated;
}

// ================================================================
// G) Idempotency
// ================================================================
export function generateIdempotencyKey() {
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function findTransactionByIdempotencyKey(transactions, key) {
  if (!key) return null;
  return (transactions || []).find((tx) => tx.idempotencyKey === key) || null;
}